import apiClient, { handleApiResponse } from './client';
import type {
  Conversion,
  ConversionHistory,
  ConversionRequest,
  ConversionFilters,
  ConversionDetails,
  ConversionStatus,
  ApiResponse,
  SourceSystem,
} from '@types';

// Backend upload response uses conversion_id, frontend uses id
interface BackendUploadResponse {
  conversion_id: number;
  status: string;
  source_system: string;
  file_name: string;
  created_at: string;
}

function mapUploadResponse(data: BackendUploadResponse): Conversion {
  return {
    id: String(data.conversion_id),
    user_id: '',
    subscription_id: '',
    source_system: data.source_system as SourceSystem,
    status: data.status as ConversionStatus,
    file_name: data.file_name,
    started_at: data.created_at,
    created_at: data.created_at,
  };
}

export const converterApi = {
  // Upload file for conversion
  uploadFile: async (request: ConversionRequest): Promise<Conversion> => {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('source_system', request.source_system);
    if (request.mapping_config_id) {
      formData.append('mapping_config_id', request.mapping_config_id);
    }

    const response = await apiClient.post<ApiResponse<BackendUploadResponse>>(
      '/v1/conversions/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return mapUploadResponse(handleApiResponse(response));
  },

  // Get conversion status
  getConversionStatus: async (conversionId: string): Promise<Conversion> => {
    const response = await apiClient.get<ApiResponse<Conversion>>(
      `/v1/conversions/status/${conversionId}`
    );
    return handleApiResponse(response);
  },

  // Get conversion history with filters
  getConversionHistory: async (
    params: ConversionFilters
  ): Promise<ConversionHistory> => {
    // Backend returns { items: [...] }, frontend expects { conversions: [...] }
    interface BackendHistoryResponse {
      items: Conversion[];
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
    }
    const response = await apiClient.get<ApiResponse<BackendHistoryResponse>>(
      '/v1/conversions/history',
      { params }
    );
    const backendData = handleApiResponse(response);
    // Map backend 'items' to frontend 'conversions'
    return {
      conversions: backendData.items ?? [],
      total: backendData.total,
      page: backendData.page,
      per_page: backendData.per_page,
      total_pages: backendData.total_pages,
    };
  },

  // Get detailed conversion information
  getConversionDetails: async (
    conversionId: string
  ): Promise<ConversionDetails> => {
    const response = await apiClient.get<ApiResponse<ConversionDetails>>(
      `/v1/conversions/${conversionId}`
    );
    return handleApiResponse(response);
  },

  // Download converted file
  downloadConvertedFile: async (conversionId: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      `/v1/conversions/download/${conversionId}`,
      {
        responseType: 'blob',
      }
    );
    return response as unknown as Blob;
  },

  // Get supported source systems
  getSupportedSystems: async (): Promise<SourceSystem[]> => {
    const response = await apiClient.get<ApiResponse<SourceSystem[]>>(
      '/v1/conversions/systems'
    );
    return handleApiResponse(response);
  },

  // Delete conversion
  deleteConversion: async (conversionId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/v1/conversions/${conversionId}`
    );
    return handleApiResponse(response);
  },

  // Retry failed conversion
  retryConversion: async (conversionId: string): Promise<Conversion> => {
    const response = await apiClient.post<ApiResponse<Conversion>>(
      `/v1/conversions/${conversionId}/retry`
    );
    return handleApiResponse(response);
  },

  // Batch upload files
  batchUpload: async (requests: ConversionRequest[]): Promise<Conversion[]> => {
    const formData = new FormData();

    requests.forEach((request, index) => {
      formData.append(`files[${index}]`, request.file);
      formData.append(`source_systems[${index}]`, request.source_system);
      if (request.mapping_config_id) {
        formData.append(
          `mapping_config_ids[${index}]`,
          request.mapping_config_id
        );
      }
    });

    const response = await apiClient.post<ApiResponse<Conversion[]>>(
      '/v1/conversions/batch-upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return handleApiResponse(response);
  },

  // Cancel conversion
  cancelConversion: async (conversionId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/v1/conversions/cancel/${conversionId}`
    );
    return handleApiResponse(response);
  },
};

export default converterApi;
